import { ecId, handBack, insertRoll, itemField, record, Refusal, requiredInt, scalar, upper, type JanusCtx, type Row } from '../context.js';
import { selectSkillForContestingRoll } from '../select-skill-for-contesting-roll.js';
import { skillRollArr, SKILL_ROLL_ALIAS } from '../skill-roll.js';
import { checkAbilitySizeCompatibility } from '../check-ability-size-compatibility.js';
import { fabricateRows } from '../../actions/ability-preconditions.js';
import { collectInventory, consumeInventory, grantInventory, inventoryValue } from './inventory.js';

export const DISENCHANT_ABILITY = 'FDCAAAC1-9502-C9F4-55C3-501C7D65192F';
export const ENCHANTING_SKILL = 'E77A8B34-F880-E0E2-E6EC-42F4241F4FCC';
export const DUST_ITEMS = ['4C02AD94-100F-25F4-612F-685A5B749043', 'CF7D7A48-3CFD-034E-FD5B-28B301CBF37F', '43B66817-419E-4993-8CFD-C96B68D74DA4'] as const;
const INTEGRATED = '36C061BF-E480-4334-961C-26500150E3C4';

function skillCheck(ctx: JanusCtx, caster: Row, source: Row, skills: unknown[], dc: number, abilityId: string, ordinaryLow = false): { natural: number; result: number } {
  // The source may be an inventory UUID: skillRollArr's used-item lookup must see that actual instance.
  const read = ctx.read, roll = ctx.roll;
  ctx.read = { ...read, definition: id => upper(id) === upper(abilityId) ? source : read.definition(id) };
  try {
    let natural = 0;
    // onNatural separates the main dice from the tier die, which can itself be d20 at tier 17.
    if (ordinaryLow) ctx.roll = { d: die => Math.min(die, natural ? 1 : 2), fraction: () => 0 };
    const result = skillRollArr(ctx, { casterId: ecId(caster), abilityId, targetIds: [], rawDiceMod: 0, rawDiceAdvDisStacks: 0,
      useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: '' }, caster,
      selectSkillForContestingRoll(ctx, caster, skills), null, dc,
      { rollType: SKILL_ROLL_ALIAS, rowType: String(read.value(source, 'roll_type') ?? itemField(source, 'item_type') ?? 'skill_roll'),
        usedRollType: upper(read.value(source, 'roll_type')), isTarget: false, onNatural: n => { natural = n; } });
    return { natural, result };
  } finally { ctx.read = read; ctx.roll = roll; }
}
export function finishGathering(ctx: JanusCtx, source: Row, caster: Row, remove: () => void): void {
  const type = ctx.read.value(source, 'roll_type');
  // Excavation shares the Gathering status but has a distinct prize/degradation contract.
  if (upper(type) !== 'FA17A3A2-3068-C84C-1EA5-C511F71FBD8B') handBack('timed-finish:gathering:source-roll-family');
  const skills = ctx.read.entries(source, 'skill_roll_contester').slice(0, 1);
  const dc = requiredInt(ctx.read.value(source, 'ability_dc'), 'timed-finish:gathering:dc-not-held');
  const outputs = fabricateRows(ctx.read.entries(source, 'fabricate'));
  if (ctx.read.has(source, 'fabricate') && !outputs.length) handBack('timed-finish:gathering:empty-recipe');
  const { result } = skillCheck(ctx, caster, source, skills, dc, upper(inventoryValue(ctx, source, 'inventory_id')));
  remove();
  if (result < dc || outputs.length) consumeInventory(ctx, source, caster);
  if (result >= dc) {
    if (outputs.length) for (const output of outputs) grantInventory(ctx, caster, output.uuid, output.qty, output.template);
    else collectInventory(ctx, source, caster, INTEGRATED);
  }
}
/** Exact legacy dust ladder, including the minimum Illusion unit and independent half-up rounding. */
export function disenchantDust(cost: number, natural: number, result: number): number[] {
  if (natural === 1) return [0, 0, 0];
  const factor = natural === 20 ? 2 : result > 1 && result < 10 ? .7 : result >= 10 ? 1 : 0;
  const illusion = Math.max(1, Math.floor(cost / 1500));
  const vision = Math.floor((cost - illusion * 500) / 50);
  const dream = Math.floor((cost - illusion * 500 - vision * 50) / 5);
  return [illusion, vision, dream].map(n => Math.max(0, Math.floor(n * factor + .5)));
}
export function disenchantRequirements(ctx: JanusCtx, source: Row, caster: Row, ability: Row): string | null {
  if (!inventoryValue(ctx, source, 'inventory_id')) return 'This item is not an inventory object, and cannot be disenchanted!';
  const cost = Number(scalar(ctx.read.value(source, 'cost_gp')) ?? 0);
  if (!Number.isFinite(cost) || cost === 0) return 'This inventory has no cost associated to it to be enchanted or it is priceless';
  if (cost < 100) return 'This inventory is worth less than 100gp, and is not high enough value to be disenchanted';
  const skills = record(record(record(caster.computed)?.creature)?.skills);
  if (!skills) handBack('timed-finish:disenchanting:skills-not-held');
  const tier = Number(scalar(record(skills.enchanting)?.tier_level) ?? 0);
  if (tier <= 0) return 'This caster does not have the enchanting skill and cannot disenchant!';
  if (upper(inventoryValue(ctx, source, 'possess_id')) !== ecId(caster)) return 'You must possess the item to disenchant it!';
  if (!checkAbilitySizeCompatibility(ctx, ability, caster, source)) return 'This inventory is outside your disenchanting size capability.';
  if (cost > tier * 1000) return `This inventory is too valuable (${cost}) gp and beyond the skill for the enchanter to disenchant (${tier * 1000}) gp.`;
  return null;
}
export function finishDisenchanting(ctx: JanusCtx, source: Row, caster: Row, remove: () => void, ordinaryLow = false): void {
  const ability = ctx.read.definition(DISENCHANT_ABILITY);
  if (!ability) handBack('timed-finish:disenchanting:ability-not-held');
  const failure = disenchantRequirements(ctx, source, caster, ability);
  if (failure) throw new Refusal(failure);
  const cost = Number(scalar(ctx.read.value(source, 'cost_gp')));
  // Legacy removes the timed row before the skill roll, without charging the start's AP again.
  remove();
  const { natural, result } = skillCheck(ctx, caster, ability, [ENCHANTING_SKILL], 10, DISENCHANT_ABILITY, ordinaryLow);
  if (natural !== 1 && natural !== 20 && result <= 1) handBack('timed-finish:disenchanting:undefined-legacy-render-factor');
  consumeInventory(ctx, source, caster);
  const log = natural === 1 ? 'all_dust_is_lost' : natural === 20 ? 'well_of_creation_connected' : result > 1 && result < 10 ? 'partial_disenchant' : null;
  if (log) insertRoll(ctx, { roll_type_1: log, roll_item_id: upper(inventoryValue(ctx, source, 'inventory_id')), by: caster });
  const yields = disenchantDust(cost, natural, result);
  yields.forEach((qty, i) => { if (qty) grantInventory(ctx, caster, DUST_ITEMS[i]!, qty); });
}
